import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire, formaterDateLongue,
} from '../helpers.jsx';
import { UserPlus, Shield, ShieldOff, Power, PowerOff, Search } from 'lucide-react';

// Liste des utilisateurs + promotion par login Dolibarr (sans pre-login requis).
export function AdminUtilisateurs({ utilisateurActuel }) {
  const [users, setUsers]       = useState([]);
  const [chargement, setCharg]  = useState(true);
  const [erreur, setErreur]     = useState('');
  const [recherche, setRech]    = useState('');
  // Promotion
  const [promoOuvert, setPromo] = useState(false);
  const [promoLogin, setPLogin] = useState('');
  const [promoAdmin, setPAdmin] = useState(true);
  const [promoEnvoi, setPEnvoi] = useState(false);
  const [promoErreur, setPErr]  = useState('');

  function recharger() {
    setCharg(true);
    api.get('/admin/utilisateurs')
      .then(d => setUsers(d.utilisateurs || []))
      .catch(e => setErreur(e.message))
      .finally(() => setCharg(false));
  }
  useEffect(recharger, []);

  async function basculerAdmin(u) {
    if (u.id === utilisateurActuel.id) {
      alert("Tu ne peux pas modifier tes propres droits admin.");
      return;
    }
    if (!confirm(`${u.est_admin ? 'Retirer les droits admin à' : 'Promouvoir admin'} ${u.nom_complet} ?`)) return;
    try {
      await api.put(`/admin/utilisateurs/${u.id}`, { est_admin: !u.est_admin });
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function basculerActif(u) {
    if (u.id === utilisateurActuel.id) {
      alert("Tu ne peux pas te désactiver toi-même.");
      return;
    }
    if (!confirm(`${u.actif ? 'Désactiver' : 'Réactiver'} ${u.nom_complet} ?`)) return;
    try {
      await api.put(`/admin/utilisateurs/${u.id}`, { actif: !u.actif });
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function promouvoir() {
    if (promoLogin.trim() === '') return;
    setPErr(''); setPEnvoi(true);
    try {
      const rep = await api.post('/admin/utilisateurs/promouvoir', {
        login_dolibarr: promoLogin.trim(),
        est_admin: promoAdmin,
      });
      setPromo(false); setPLogin(''); setPAdmin(true);
      recharger();
    } catch (e) {
      setPErr(e instanceof ErreurApi ? e.message : 'Erreur');
    } finally {
      setPEnvoi(false);
    }
  }

  const usersFiltrés = users.filter(u =>
    !recherche
    || u.login.toLowerCase().includes(recherche.toLowerCase())
    || u.nom_complet.toLowerCase().includes(recherche.toLowerCase())
    || (u.email || '').toLowerCase().includes(recherche.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
            Utilisateurs
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
            Les fiches sont créées automatiquement à la première connexion Dolibarr.
            Tu peux aussi pré-créer + promouvoir un compte ci-dessous.
          </p>
        </div>
        <button onClick={() => setPromo(true)} style={boutonPrincipal}>
          <UserPlus size={16} /> Ajouter / promouvoir
        </button>
      </div>

      <div style={{ position:'relative', maxWidth: 360, marginBottom: 14 }}>
        <Search
          size={14}
          color={tokens.inkMuted}
          style={{ position:'absolute', left: 10, top: 11, pointerEvents:'none' }}
        />
        <input
          value={recherche}
          onChange={(e) => setRech(e.target.value)}
          placeholder="Rechercher par login, nom ou email"
          style={{ ...inputBase, paddingLeft: 32 }}
        />
      </div>

      {erreur && <BoiteErreur message={erreur} />}
      {chargement && <div style={etat}>Chargement…</div>}

      {!chargement && usersFiltrés.length === 0 && (
        <div style={etat}>Aucun utilisateur</div>
      )}

      {!chargement && usersFiltrés.length > 0 && (
        <div style={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse:'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: tokens.surfaceAlt, color: tokens.inkSoft, fontSize: 11, textTransform:'uppercase', letterSpacing: 0.3 }}>
                <Th>Utilisateur</Th>
                <Th>Login</Th>
                <Th>Email</Th>
                <Th>Tickets</Th>
                <Th>Dernière connexion</Th>
                <Th>Statut</Th>
                <Th style={{ textAlign:'right' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {usersFiltrés.map(u => (
                <tr key={u.id} style={{ borderTop: `1px solid ${tokens.border}` }}>
                  <Td>
                    <div style={{ fontWeight: 500, color: tokens.ink }}>{u.nom_complet}</div>
                    {u.id === utilisateurActuel.id && (
                      <span style={{ fontSize: 10, color: tokens.inkMuted }}>(toi)</span>
                    )}
                  </Td>
                  <Td><code style={{ fontSize: 11 }}>{u.login}</code></Td>
                  <Td><span style={{ color: tokens.inkSoft, fontSize: 12 }}>{u.email || '—'}</span></Td>
                  <Td>{u.nb_tickets}</Td>
                  <Td><span style={{ color: tokens.inkMuted, fontSize: 11 }}>
                    {u.dernier_acces ? formaterDateLongue(u.dernier_acces) : 'Jamais'}
                  </span></Td>
                  <Td>
                    {u.est_admin && <Badge bg={tokens.accentBg} fg={tokens.accent} taille="petit">Admin</Badge>}
                    {!u.actif && <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Désactivé</Badge>}
                  </Td>
                  <Td style={{ textAlign:'right' }}>
                    <button
                      onClick={() => basculerAdmin(u)}
                      title={u.est_admin ? "Retirer admin" : "Promouvoir admin"}
                      style={iconBtn}
                    >
                      {u.est_admin ? <ShieldOff size={14} /> : <Shield size={14} />}
                    </button>
                    <button
                      onClick={() => basculerActif(u)}
                      title={u.actif ? "Désactiver" : "Réactiver"}
                      style={{ ...iconBtn, color: u.actif ? tokens.danger : tokens.success }}
                    >
                      {u.actif ? <PowerOff size={14} /> : <Power size={14} />}
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {promoOuvert && (
        <div style={{
          position:'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', padding: 20,
        }}>
          <div style={{ background: tokens.surface, borderRadius: 12, maxWidth: 440, width:'100%', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.20)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>
              Ajouter / promouvoir un utilisateur
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: tokens.inkSoft }}>
              Saisis le <strong>login Dolibarr</strong>. Sa fiche sera créée (s'il n'existe
              pas encore) avec les infos récupérées de Dolibarr. Pas besoin qu'il se soit déjà connecté.
            </p>

            <label style={labelChamp}>Login Dolibarr</label>
            <input
              autoFocus
              value={promoLogin}
              onChange={(e) => setPLogin(e.target.value)}
              placeholder="Ex: marine.garoche"
              style={inputBase}
            />

            <label style={{ ...labelChamp, marginTop: 14, display:'flex', alignItems:'center', gap: 8 }}>
              <input type="checkbox" checked={promoAdmin} onChange={(e) => setPAdmin(e.target.checked)} />
              Lui donner les droits admin
            </label>

            {promoErreur && <BoiteErreur message={promoErreur} />}

            <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 18 }}>
              <button onClick={() => setPromo(false)} style={boutonSecondaire}>Annuler</button>
              <button
                onClick={promouvoir}
                disabled={promoEnvoi || promoLogin.trim() === ''}
                style={boutonPrincipal}
              >
                {promoEnvoi ? 'Création…' : 'Créer / Promouvoir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, style }) => (
  <th style={{ padding: '10px 12px', textAlign:'left', fontWeight: 600, ...style }}>{children}</th>
);
const Td = ({ children, style }) => (
  <td style={{ padding: '10px 12px', ...style }}>{children}</td>
);
const iconBtn = {
  background:'transparent', border:'none', cursor:'pointer',
  padding: 6, marginLeft: 4, color: tokens.inkSoft,
  borderRadius: 4,
};
const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32,
  textAlign:'center',
  color: tokens.inkSoft,
  fontSize: 14,
};
